import React from "react";
import {observer} from "mobx-react-lite";
import {generatePath, useNavigate} from "react-router";
import {Button, Paper, Stack, Typography} from "@mui/material";
import {CategoryOutlined} from "@mui/icons-material";
import {useStores} from "~/stores";
import {routes} from "~/routing/routes";
import {MultiSelection} from "~/components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {toTrimmedCategoryArray} from "~/services/classes/CategoryHelpers";
import {PATH_SEPARATOR} from "~/pages/category-profile/domain/exploreTree";
import {categoryDisplayLabel} from "~/pages/spend-landscape/filters/categoryOptions";
import {useCategoryProfileCardVisible} from "~/pages/category-profile/hooks/useCategoryProfileDisplay";

/**
 * Shows the selected category (and supplier) and opens its Category Profile.
 * Only shown when one category is selected and the customer has the Category Profile.
 */
export const CategoryProfileBar: React.FC = observer(() => {
    const {bagStore} = useStores();
    const categoryProfileVisible = useCategoryProfileCardVisible();
    const navigate = useNavigate();
    const manager = bagStore.advanced;
    const path = toTrimmedCategoryArray(manager.singleSelectedCategory);
    if (path.length === 0 || !categoryProfileVisible) return null;

    // The supplier name is known from the table once the page is filtered on one supplier
    const s_ids = manager.filters.s_ids;
    const supplierId = MultiSelection.isIncludingOne(s_ids) ? String(s_ids?.selected[0].id) : null;
    const supplierName = supplierId
        ? manager.advancedSupplierBreakdownDataRM.result?.data.rows.find(r => String(r.s_id) === supplierId)?.s_name
        : undefined;

    const open = () => {
        const target = generatePath(routes.category_profile, {id: String(bagStore.bagId), tab: 'overview'});
        navigate(`${target}?${new URLSearchParams({path: path.join(PATH_SEPARATOR)})}`);
    };

    return <Paper variant="outlined" className="spend-landscape-profile-bar">
        <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">Category profile context</Typography>
            <Typography variant="body2" fontWeight={600} data-testid="category-profile-path">
                {path.map(categoryDisplayLabel).join(' › ')}
            </Typography>
            {supplierName && <Typography variant="body2" color="text.secondary">{supplierName}</Typography>}
            <span style={{flexGrow: 1}}/>
            <Button variant="contained" disableElevation startIcon={<CategoryOutlined/>} onClick={open}>
                Go to category profile
            </Button>
        </Stack>
    </Paper>;
});
